import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "antd";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import PageHeader from "components/Common/PageHeader";
import { all_routes } from "router/all_routes";
import CustomButton from "components/Common/ui/CustomButton";
import TableFilterDynamique, { FilterField } from "components/Common/TableFilter/TableFilterDynamique";
import Table from "components/Common/dataTable";

import AssignStudentsModal from "../components/AssignStudentsModal";
import AddEditClassModal from "../components/AddEditClassModal";
import AssignSubjectsModal from "../components/AssignSubjectsModal";

import ActionsDropdown, { Action } from "components/Common/ActionsDropdown";
import useClassManagement from "../hooks/useClassManagement";

import { ClassData, StudentData } from "../../../../types/classTypes";
import { CLASS_SECTION_OPTIONS_BY_LEVEL } from "../../../../constants/classConstants";
import { getBranchLabelFromCode, getClassLevelLabelFromCode } from "utils/schoolUtils";
import { useAbility } from "config/abilityContext";
import AddClassFeeModal from "feature-module/finance/modals/AddClassFeeModal";
import useFee from "feature-module/finance/hooks/useFee";

const ClassList: React.FC = () => {
  const { t } = useTranslation();
  const ability = useAbility();
  const [searchParams] = useSearchParams();

  const {
    classesIntance,
    subjects,
    assignedStudents,
    allStudents,
    isAssignModalOpen,
    isAddEditModalOpen,
    editingClass,
    currentClassId,
    openAssignModal,
    openAddEditModal,
    handleAssignStudentsSave,
    handleSaveClass,
    handleDeleteClass,
    setAssignModalOpen,
    setAddEditModalOpen,
    classLevels,
    fetchClassLevels,
    loading,
    fetchClassesWithPagination,
    pagination,
    fetchClasses,
    classes
  } = useClassManagement();


  const {
    fetchFeeTypes,
    feeTypes,
    handleCreateByClassId,
  } = useFee();

  const [isAssignSubjectModalOpen, setIsAssignSubjectModalOpen] = useState(false);
  const [isAddClassFeeModalOpen, setIsAddClassFeeModalOpen] = useState(false); // State for add fee modal
  const [selectedClassForFee, setSelectedClassForFee] = useState<string | null>(null); // State for selected class
  const [modalSubjects, setModalSubjects] = useState<any[]>([]);
  const [filters, setFilters] = useState<{ [key: string]: string | number | null }>({});

  const breadcrumbItems = [
    { label: "dashboard", path: all_routes.dashboard },
    { label: "classes_list", active: true },
  ];

  const classLevelOptions = useMemo(
    () => (classLevels || []).map((level: any) => ({ label: getClassLevelLabelFromCode(level.labelKey), value: level.id })),
    [classLevels]
  );

  const sectionOptions = useMemo(
    () => CLASS_SECTION_OPTIONS_BY_LEVEL(t).map((option: any) => ({ label: option.label, value: option.value })),
    [t]
  );

  const filterFields: FilterField[] = useMemo(
    () => [
      { key: "name", label: t("class"), type: "text" },
      { key: "classLevelId", label: t("level"), type: "select", options: classLevelOptions },
      { key: "schoolCycle", label: t("section"), type: "select", options: sectionOptions },
    ],
    [t, classLevelOptions, sectionOptions]
  );

  const openSubjectsModal = (record: ClassData) => {
    const extractedSubjects =
      record.classSubject?.map((cs) => ({
        name: cs.subject?.name || t("unknown_subject"),
        coefficient: cs.coefficient,
        colorCode: cs.subject?.colorCode,
        code: cs.subject?.code,
        parent: cs.subject?.parent ?? null,
      })) || [];

    setModalSubjects(extractedSubjects);
    setIsAssignSubjectModalOpen(true);
  };

  const openAddFeeModal = (record: ClassData) => {
    setSelectedClassForFee(record.id);
    setIsAddClassFeeModalOpen(true);
  };

  const handleDeleteClassWithRefresh = async (classId: string) => {
    Modal.confirm({
      title: t("confirm_delete"),
      content: t("delete_class_warning"),
      okText: t("delete"),
      okType: "danger",
      cancelText: t("cancel"),
      async onOk() {
        const res = await handleDeleteClass(classId);
        if (!res) return;
        const currentPage = pagination?.page || 1;
        const pageSize = pagination?.limit || 10;
        const remainingItems = (pagination?.total || 0) - 1;

        const maxPageAfterDelete = Math.ceil(remainingItems / pageSize);

        const nextPage =
          currentPage > maxPageAfterDelete
            ? Math.max(maxPageAfterDelete, 1)
            : currentPage;

        fetchClassesWithPagination({
          ...filters,
          page: nextPage,
          limit: pageSize,
        });

      },
    });
  };

  const columns: Array<{
    title: string;
    dataIndex?: keyof ClassData;
    sorter?: (a: ClassData, b: ClassData) => number;
    render?: (text: any, record: ClassData) => JSX.Element | string;
  }> = [
      {
        title: t("class"),
        dataIndex: "name",
      },
      {
        title: t("class_code"),
        dataIndex: "code",
      },
      {
        title: t("section"),
        dataIndex: "section",
        render: (_, record) => t(record.classLevel?.schoolCycle || ""),
      },
      {
        title: t("level"),
        dataIndex: "level",
        render: (_, record) => getClassLevelLabelFromCode(record.classLevel?.labelKey ?? ""),
      },
      {
        title: t("branch"),
        dataIndex: "branch",
        render: (_, record) =>
          record.branch ? getBranchLabelFromCode(record.branch?.labelKey ?? "", t) : t("no_branch"),
      },
      {
        title: t("no_of_students"),
        dataIndex: "noOfStudents",
        render: (_, record) => (
          <Link to={all_routes.detailClass.replace(":classId", record.id) + "?tab=students"}>
            {record.noOfStudents || 0}
          </Link>
        ),
      },
      {
        title: t("action"),
        render: (_, record) => {
          const actions: Action[] = [
            {
              label: t("view_details"),
              icon: "ti ti-eye",
              actionType: "link",
              hidden: !ability.can("getAll", "Class"),
              to: `${all_routes.detailClass.replace(":classId", record.id)}`,
            },
            {
              label: t("assign_students"),
              icon: "ti ti-users",
              actionType: "modal",
              hidden: !ability.can("update", "Class"),
              onClick: () => openAssignModal(record.id),
            },
            {
              label: t("edit"),
              icon: "ti ti-edit-circle",
              actionType: "modal",
              hidden: !ability.can("update", "Class"),
              onClick: () => openAddEditModal(record),
            },
            {
              label: t("add_fee_to_class"),
              icon: "ti ti-currency-dollar",
              actionType: "modal",
              hidden: !ability.can("create", "Fee"),
              onClick: () => openAddFeeModal(record),
            },
            {
              label: t("view_subjects"),
              icon: "ti ti-book",
              actionType: "modal",
              onClick: () => openSubjectsModal(record),
            },
            {
              label: t("delete"),
              icon: "ti ti-trash",
              actionType: "modal",
              hidden: !ability.can("delete", "Class"),
              onClick: () => handleDeleteClassWithRefresh(record.id),
            },
          ];

          return <ActionsDropdown actions={actions} />;
        },
      },
    ];

  const handleFilter = (newFilters: { [key: string]: any }) => {
    setFilters(newFilters);

    fetchClassesWithPagination({
      ...newFilters,
      page: 1,
      limit: 10,
    });
  };

  useEffect(() => {
    const shouldShowModal = searchParams.get("showAddModal") === "true";
    setAddEditModalOpen(shouldShowModal);

    fetchClassLevels();
    fetchClassesWithPagination({
      page: 1,
      limit: 10,
    });
  }, []);

  const refresh = () => {
    fetchClassesWithPagination({
      ...filters,
      page: pagination?.page || 1,
      limit: pagination?.limit || 10,
    });
  };

  const handleSaveClassWithRefresh = async (classData: Partial<ClassData>) => {
    const res = await handleSaveClass(classData);
    if (res) refresh();
  };

  const handleAssignStudentsSaveWithRefresh = async (classId: string, students: StudentData[]) => {
    const res = await handleAssignStudentsSave(classId, students);
    if (res) refresh();
  };

  const handleSaveFee = async (feeData: any) => {
    const result = await handleCreateByClassId(feeData);
    if (result) {
      setIsAddClassFeeModalOpen(false);
      setSelectedClassForFee(null);
    }
    return result;
  };


  useEffect(() => {
    (async () => {
      await fetchFeeTypes();
      await fetchClasses();
    })();
  }, []);

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader title={t("classes_list")} breadcrumbItems={breadcrumbItems} />

        <div className="d-flex mb-3 justify-content-end gap-2">
          {ability.can("create", "Class") && (
            <CustomButton onClick={() => openAddEditModal()} variant="primary" size="md" className="btn-primary">
              <i className="ti ti-plus me-2" />
              {t("add_class")}
            </CustomButton>
          )}

          <TableFilterDynamique
            fields={filterFields}
            filters={filters}
            onApply={handleFilter}
            onReset={() => handleFilter({ page: 1, limit: 10 })}
          />
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={classesIntance}
          loading={loading}
          pagination={true}
          dataPaginate={{
            current: pagination?.page || 1,
            pageSize: pagination?.limit || 10,
            total: pagination?.total || 0,
          }}
          onChange={(pg: any) => {
            fetchClassesWithPagination({
              ...filters,
              page: pg.current!,
              limit: pg.pageSize!,
            });
            setFilters((prev) => ({
              ...prev,
              page: pg.current!,
              limit: pg.pageSize!,
            }));
          }}
        />

        <AssignStudentsModal
          classId={currentClassId || ""}
          isOpen={isAssignModalOpen}
          allStudents={allStudents}
          assignedStudents={assignedStudents}
          onClose={() => setAssignModalOpen(false)}
          onSave={handleAssignStudentsSaveWithRefresh}
        />

        <AddEditClassModal
          classData={editingClass}
          isOpen={isAddEditModalOpen}
          onSave={handleSaveClassWithRefresh}
          onClose={() => setAddEditModalOpen(false)}
          subjects={subjects}
          classLevels={classLevels}
        />

        <AssignSubjectsModal
          isOpen={isAssignSubjectModalOpen}
          onClose={() => setIsAssignSubjectModalOpen(false)}
          subjects={modalSubjects}
        />

        <AddClassFeeModal
          students={allStudents}
          isOpen={isAddClassFeeModalOpen}
          onClose={() => {
            setIsAddClassFeeModalOpen(false);
            setSelectedClassForFee(null);
          }}
          onSave={handleSaveFee}
          feeTypes={feeTypes}
          classes={classes}
          selectedClass={selectedClassForFee || undefined}
        />
      </div>
    </div>
  );
};

export default ClassList;